import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { themeUtils } from '../../../theme';

const StyledDataGrid = styled(DataGrid)(({ theme, hideHorizontalScrollbar }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 0,
  backgroundColor: 'white',
  '& .MuiDataGrid-main': {
    borderRadius: 0,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: themeUtils.colors.blue['blue-50'],
    borderBottom: `2px solid ${themeUtils.colors.blue['blue-100']}`,
    '& .MuiDataGrid-columnHeader': {
      fontWeight: 600,
      fontSize: '0.875rem',
      color: theme.palette.primary.main,
    },
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
    '&.Mui-selected': {
      backgroundColor: `${theme.palette.primary.main}08`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.main}12`,
      },
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    display: 'flex',
    alignItems: 'center',
    '& .MuiDataGrid-cellContent': {
      width: '100%',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.grey[50],
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.grey[25],
  },
  // Conditionally hide horizontal scrollbar
  ...(hideHorizontalScrollbar && {
    '& .MuiDataGrid-main': {
      overflowX: 'hidden',
    },
    '& .MuiDataGrid-virtualScroller': {
      overflowX: 'hidden !important',
      overflowY: 'auto',
    },
    '& .MuiDataGrid-virtualScrollerContent': {
      width: '100% !important',
      overflow: 'hidden',
    },
    '& .MuiDataGrid-virtualScrollerRenderZone': {
      width: '100% !important',
      overflow: 'hidden',
    },
    '& .MuiDataGrid-scrollbar--horizontal': {
      display: 'none !important',
    },
    '& .MuiDataGrid-columnHeaders': {
      width: '100% !important',
      overflowX: 'hidden',
    },
    '& .MuiDataGrid-row': {
      maxWidth: '100% !important',
    },
  }),
}));

const EasyCheckDataGrid = ({
  rows = [],
  columns = [],
  loading = false,
  checkboxSelection = false,
  disableRowSelectionOnClick = true,
  rowHeight = 52,
  headerHeight = 56,
  paginationMode = 'client',
  pageSize = 25,
  rowsPerPageOptions = [10, 25, 50, 100],
  disableColumnFilter = false,
  disableColumnMenu = false,
  hideHorizontalScrollbar = false,
  scrollbarSize = 17,
  autoWidth = false,
  density = 'compact',
  ...props
}) => {
  return (
    <StyledDataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      rowHeight={rowHeight}
      headerHeight={headerHeight}
      paginationMode={paginationMode}
      density={density}
      initialState={{
        pagination: {
          paginationModel: { pageSize },
        },
      }}
      pageSizeOptions={rowsPerPageOptions}
      disableColumnFilter={disableColumnFilter}
      disableColumnMenu={disableColumnMenu}
      hideHorizontalScrollbar={hideHorizontalScrollbar}
      scrollbarSize={scrollbarSize}
      sx={{
        width: '100%',
        ...(autoWidth && {
          '& .MuiDataGrid-columnHeaders': {
            minWidth: 'fit-content',
          },
          '& .MuiDataGrid-row': {
            minWidth: 'fit-content',
          },
        }),
      }}
      {...props}
    />
  );
};

EasyCheckDataGrid.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.array,
  loading: PropTypes.bool,
  checkboxSelection: PropTypes.bool,
  disableRowSelectionOnClick: PropTypes.bool,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  paginationMode: PropTypes.oneOf(['client', 'server']),
  pageSize: PropTypes.number,
  rowsPerPageOptions: PropTypes.array,
  disableColumnFilter: PropTypes.bool,
  disableColumnMenu: PropTypes.bool,
  hideHorizontalScrollbar: PropTypes.bool,
  scrollbarSize: PropTypes.number,
  autoWidth: PropTypes.bool,
  density: PropTypes.oneOf(['compact', 'standard', 'comfortable']),
};

export default EasyCheckDataGrid; 